import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Paperclip } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { playDing } from "../lib/notifySound";

export const MSG_UNREAD_EVENT = "pgp-msg-unread";
export const MSG_REFRESH_EVENT = "pgp-msg-refresh";
export const ATT_UNREAD_EVENT = "pgp-att-unread";
export const ATT_REFRESH_EVENT = "pgp-att-refresh";

const readSeen = (key) => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch (e) { return []; } };
export const markNotified = (userId, id) => {
  const key = `pgp_msg_notified_${userId}`;
  localStorage.setItem(key, JSON.stringify([...readSeen(key), id].slice(-500)));
};
export const allegatiUrl = (it) => `/app/pratiche?p=${it.pratica_id}&allegati=${encodeURIComponent(it.assistito_key || "principale")}`;

export const NewMessageNotifier = () => {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const staff = !!user && !["cliente", "superadmin"].includes(user.role);
  const msgOn = staff && can("messaggi");
  const attOn = staff && can("pratiche");
  const seenKey = `pgp_msg_notified_${user?.id}`;

  const known = useRef(new Set());
  const enqueue = useCallback((list) => {
    const seen = new Set(readSeen(seenKey));
    const fresh = list.filter((m) => !seen.has(m.key) && !known.current.has(m.key));
    if (!fresh.length) return;
    fresh.forEach((m) => known.current.add(m.key));
    playDing();
    setQueue((q) => [...q, ...fresh]);
  }, [seenKey]);

  const poll = useCallback(() => {
    api.get("/studio/messaggi/unread").then(({ data }) => {
      window.dispatchEvent(new CustomEvent(MSG_UNREAD_EVENT, { detail: data.total }));
      enqueue(data.messages.map((m) => ({ ...m, kind: "msg", key: m.id })));
    }).catch(() => {});
  }, [enqueue]);

  const pollAtt = useCallback(() => {
    api.get("/studio/allegati-cliente/unread").then(({ data }) => {
      window.dispatchEvent(new CustomEvent(ATT_UNREAD_EVENT, { detail: data.total }));
      enqueue(data.items.map((a) => ({ ...a, kind: "att", key: `att:${a.id}` })));
    }).catch(() => {});
  }, [enqueue]);

  useEffect(() => {
    if (!msgOn) return;
    poll();
    const id = setInterval(poll, 30000);
    window.addEventListener(MSG_REFRESH_EVENT, poll);
    return () => { clearInterval(id); window.removeEventListener(MSG_REFRESH_EVENT, poll); };
  }, [msgOn, poll]);

  useEffect(() => {
    if (!attOn) return;
    pollAtt();
    const id = setInterval(pollAtt, 30000);
    window.addEventListener(ATT_REFRESH_EVENT, pollAtt);
    return () => { clearInterval(id); window.removeEventListener(ATT_REFRESH_EVENT, pollAtt); };
  }, [attOn, pollAtt]);

  const current = queue[0];
  if (!current) return null;
  const isAtt = current.kind === "att";

  const dismiss = () => {
    localStorage.setItem(seenKey, JSON.stringify([...readSeen(seenKey), current.key].slice(-500)));
    setQueue((q) => q.slice(1));
  };
  const openNow = () => { dismiss(); navigate(isAtt ? allegatiUrl(current) : `/app/messaggi?p=${current.pratica_id}`); };
  const Ico = isAtt ? Paperclip : MessageSquare;
  const tid = isAtt ? "new-attachment" : "new-message";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" data-testid={`${tid}-popup`}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl pgp-fade-up">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 shrink-0 rounded-full bg-[#F3F4F6] flex items-center justify-center">
            <Ico className="h-5 w-5 text-[#111827]" />
          </div>
          {isAtt ? (
            <p className="text-base font-semibold text-[#111827] break-words" data-testid="new-attachment-popup-text">
              Nuovo allegato da {current.cliente_nome}: <span className="font-normal" data-testid="new-attachment-file">{current.file}</span> - Pratica{" "}
              <button type="button" onClick={openNow} data-testid="new-attachment-pratica-link"
                className="text-[#1d4ed8] underline underline-offset-2 hover:text-[#1e40af] transition-colors">{current.numero_pratica}</button>
            </p>
          ) : (
            <p className="text-base font-semibold text-[#111827] break-words" data-testid="new-message-popup-text">
              Nuovo messaggio da {current.cliente_nome} - Pratica {current.numero_pratica}
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={dismiss} data-testid={`${tid}-later`}
            className="rounded-lg border border-[#d1d5db] px-4 py-2 text-sm font-medium text-[#111827] hover:bg-[#F3F4F6] transition-colors">Vedi dopo</button>
          <button type="button" onClick={openNow} data-testid={`${tid}-open`}
            className="rounded-lg bg-[#1d4ed8] px-4 py-2 text-sm font-semibold text-[#ffffff] hover:bg-[#1e40af] transition-colors">Vedi subito</button>
        </div>
      </div>
    </div>
  );
};
